// src/features/pos/pages/POSPage.tsx

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { usePosStore, PaymentItem } from '@/store/posStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { POSHeader } from './components/POSHeader';
import { CartPanel } from './components/CartPanel';
import { PaymentModal } from './components/PaymentModal';
import { CategoryPanel } from './components/CategoryPanel';
import { ProductCard } from './components/ProductCard';
import { ProductSearch } from './components/ProductSearch';
import { CustomerModal } from './components/CustomerModal';
import { ShiftModal } from './components/ShiftModal';
import { ShiftCloseModal } from './components/ShiftCloseModal';
import { ReceiptModal } from './components/ReceiptModal';
import { VoidSaleModal } from './components/VoidSaleModal';
import { KitchenView } from './components/KitchenView';
import {
    getActiveShift, openShift, parkCart, checkout, directCheckout,
    findCustomerByPhone, searchProducts, getCategories,
    batchCheckStockConfirmation,
    getPosTerminals,
    lookupBarcode,
} from './services/posApi';
import { getBranches, getMyPosPermissions } from '@/features/settings/services/settingsApi';
import { Grid3x3, List, SlidersHorizontal, WifiOff, ShieldX, ShieldCheck, AlertTriangle, ShoppingCart, Clock, X, DoorOpen } from 'lucide-react';
import { clsx } from 'clsx';
import apiClient from '@/lib/api-client';
import { useNavigate } from 'react-router-dom';

const TAX_RATE = 0;
const LOW_STOCK_THRESHOLD = 10;
const VOID_SUPERVISOR_THRESHOLD = 2000;
const MAX_SINGLE_ITEM_QUANTITY = 100;

function playPosBeep(type: 'success' | 'error' = 'success') {
    try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        if (type === 'success') {
            osc.type = 'sine';
            osc.frequency.setValueAtTime(1400, ctx.currentTime);
            osc.frequency.setValueAtTime(1800, ctx.currentTime + 0.08);
            gain.gain.setValueAtTime(0.12, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.16);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.16);
        } else {
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(320, ctx.currentTime);
            osc.frequency.setValueAtTime(240, ctx.currentTime + 0.12);
            gain.gain.setValueAtTime(0.15, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.28);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.28);
        }
    } catch { }
}

function getJwtValue(key: string): string {
    try {
        const token = localStorage.getItem('accessToken');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload[key] || '';
        }
    } catch { }
    return '';
}

function isSuperAdminFromJwt(): boolean {
    try {
        const token = localStorage.getItem('accessToken');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.isSuperAdmin || false;
        }
    } catch { }
    return false;
}

export function POSPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const pos = usePosStore();
    const addToast = useToastStore((s) => s.addToast);
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => { pos.setCartContext('retail'); }, []);

    const tenantId = user?.tenantId || getJwtValue('tenantId');
    const authSelectedBranchId = useAuthStore((s) => s.selectedBranchId) || getJwtValue('selectedBranchId');

    // ✅ FIXED: Safe POS access state
    const [posAccess, setPosAccess] = useState<string>(user?.posAccess || getJwtValue('posAccess') || '');
    const [isCheckingAccess, setIsCheckingAccess] = useState(!user?.posAccess && !getJwtValue('posAccess'));
    const isSuperAdmin = isSuperAdminFromJwt();

    useEffect(() => {
        if (user?.posAccess || getJwtValue('posAccess')) {
            setPosAccess(user?.posAccess || getJwtValue('posAccess') || 'none');
            setIsCheckingAccess(false);
            return;
        }

        getMyPosPermissions()
            .then((access) => {
                setPosAccess(access || 'none');
                setIsCheckingAccess(false);
            })
            .catch(() => {
                setPosAccess(getJwtValue('posAccess') || 'none');
                setIsCheckingAccess(false);
            });
    }, [user]);

    // State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [showCategoryPanel, setShowCategoryPanel] = useState(true);
    const [gridLayout, setGridLayout] = useState<'grid' | 'list'>('grid');
    const [showShiftModal, setShowShiftModal] = useState(false);
    const [showShiftCloseModal, setShowShiftCloseModal] = useState(false);
    const [showActiveShiftPrompt, setShowActiveShiftPrompt] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [showVoidModal, setShowVoidModal] = useState(false);
    const [openingCash, setOpeningCash] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [lastSaleId, setLastSaleId] = useState('');
    const [lastTotal, setLastTotal] = useState(0);
    const [lastOrderNumber, setLastOrderNumber] = useState('');
    const [isKitchenView, setIsKitchenView] = useState(false);
    const [showCustomerDisplay, setShowCustomerDisplay] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [stockStatusMap, setStockStatusMap] = useState<Record<string, {
        confirmed: boolean; availableQuantity: string; isLow: boolean; isOut: boolean;
        hasRecord: boolean; confirmationStatus: string;
    }>>({});

    const [currentTerminalId, setCurrentTerminalId] = useState<string>('');
    const [terminalDefaultBankAccount, setTerminalDefaultBankAccount] = useState<string>('');
    const [terminalDefaultCashAccount, setTerminalDefaultCashAccount] = useState<string>('');

    // ✅ NEW: State for branch selection in ShiftModal
    const [selectedBranchId, setSelectedBranchId] = useState('');

    // Online/Offline
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
    }, []);

    // ==================== QUERIES ====================

    // ✅ UPDATED: Shift query with posType
    const { data: shift, isLoading: shiftLoading, refetch: refetchShift } = useQuery({
        queryKey: ['active-shift', tenantId, 'retail'],
        queryFn: () => getActiveShift(tenantId, 'retail'),
        enabled: !!tenantId, retry: false,
    });

    const { data: branches } = useQuery({
        queryKey: ['branches-pos', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const { data: categories } = useQuery({
        queryKey: ['pos-categories', tenantId],
        queryFn: () => getCategories(tenantId),
        enabled: !!tenantId,
    });

    // Query active gift hampers
    const { data: hampers } = useQuery({
        queryKey: ['pos-hampers', tenantId],
        queryFn: async () => {
            try {
                const res = await apiClient.get(`/tenants/${tenantId}/hampers`);
                const all = res.data?.data || res.data || [];
                return Array.isArray(all) ? all.filter((h: any) => h.isActive) : [];
            } catch {
                return [];
            }
        },
        enabled: !!tenantId,
    });

    const effectiveBranchId = pos.branchId || authSelectedBranchId || (branches && branches.length > 0 ? branches[0].id : '');

    // ✅ NEW: Initialize selectedBranchId when branches load and no selection yet
    useEffect(() => {
        if (branches && branches.length > 0 && !selectedBranchId) {
            const main = branches.find((b: any) => b.isMainBranch) || branches[0];
            setSelectedBranchId(main.id);
        }
    }, [branches, selectedBranchId]);

    const { data: products, isLoading: productsLoading } = useQuery({
        queryKey: ['pos-products', tenantId, selectedCategoryId, searchQuery, effectiveBranchId],
        queryFn: () => searchProducts(
            tenantId,
            searchQuery || undefined,
            (selectedCategoryId && selectedCategoryId !== 'clearance' && selectedCategoryId !== 'hampers') ? selectedCategoryId : undefined,
            effectiveBranchId || undefined
        ),
        enabled: !!tenantId,
    });

    const flatCategories = categories || [];

    // ==================== TERMINAL DETECTION ====================

    useEffect(() => {
        if (!tenantId || !effectiveBranchId) return;
        const params = new URLSearchParams(window.location.search);
        const terminalParam = params.get('terminal');
        getPosTerminals(tenantId).then(terminals => {
            let found: any = null;
            if (terminalParam) {
                found = terminals.find((t: any) => t.name === terminalParam || t.id === terminalParam || t.deviceId === terminalParam);
            } else {
                found = terminals.find((t: any) => t.branchId === effectiveBranchId && t.isActive);
            }
            if (found) {
                setCurrentTerminalId(found.id);
                if (found.defaultBankAccountId) setTerminalDefaultBankAccount(found.defaultBankAccountId);
                if (found.defaultCashAccountId) setTerminalDefaultCashAccount(found.defaultCashAccountId);
            }
        }).catch(() => { });
    }, [tenantId, effectiveBranchId]);

    // ==================== ENRICH PRODUCTS & HAMPERS ====================

    const displayProducts = useMemo(() => {
        // Special Category: Hampers
        if (selectedCategoryId === 'hampers') {
            const hampersList = (hampers || []) as any[];
            return hampersList.map((h: any) => ({
                id: `hamper-${h.id}`,
                name: `🎁 ${h.name}`,
                slug: h.slug || '',
                retailPrice: h.sellingPrice || '0',
                costPrice: h.totalCost || '0',
                barcode: h.barcode || h.sku || `HAMP-${h.id.slice(0, 8)}`,
                sku: h.sku || `HAMP-${h.id.slice(0, 8)}`,
                isConfirmed: true,
                availableQuantity: '99',
                isLowStock: false,
                isOutOfStock: false,
                isUnverified: false,
                confirmationStatus: 'confirmed',
                uoms: [{ id: 'uom-hamper', name: 'Hamper', abbreviation: 'bundle', isBaseUom: true }],
                imageUrls: [] as string[],
                brand: 'Gift Hamper',
                categoryName: 'Hampers',
                isHamper: true,
                originalHamper: h,
            }));
        }

        if (!products) return [];

        let prods = products;
        // Special Category: Clearance
        if (selectedCategoryId === 'clearance') {
            prods = prods.filter((p: any) => p.isClearance === true || !!p.clearancePrice);
        }

        return prods.map(p => {
            const status = stockStatusMap[p.id];
            const available = parseFloat(status?.availableQuantity || '0');
            const hasStockRecord = status?.hasRecord ?? false;
            const isConfirmed = status?.confirmed ?? false;

            return {
                ...p,
                isLowStock: hasStockRecord && available > 0 && available <= LOW_STOCK_THRESHOLD,
                isOutOfStock: hasStockRecord && available <= 0,
                availableQuantity: status?.availableQuantity || '0',
                isConfirmed: isConfirmed,
                isUnverified: !hasStockRecord || !isConfirmed,
                confirmationStatus: status?.confirmationStatus || 'pending',
            };
        });
    }, [products, stockStatusMap, selectedCategoryId, hampers]);

    useEffect(() => {
        if (products && products.length > 0 && tenantId && effectiveBranchId) {
            const ids = products.map(p => p.id).filter(Boolean);
            if (ids.length > 0) {
                batchCheckStockConfirmation(tenantId, ids, effectiveBranchId)
                    .then(data => {
                        const enriched: Record<string, any> = {};
                        Object.entries(data).forEach(([id, status]: [string, any]) => {
                            const available = parseFloat(status?.availableQuantity || '0');
                            const hasRecord = status?.hasRecord === true;
                            enriched[id] = {
                                confirmed: hasRecord ? (status?.confirmed ?? false) : false,
                                availableQuantity: hasRecord ? (status?.availableQuantity || '0') : '0',
                                hasRecord: hasRecord,
                                isLow: hasRecord && available > 0 && available <= LOW_STOCK_THRESHOLD,
                                isOut: hasRecord && available <= 0,
                                confirmationStatus: status?.confirmationStatus || 'pending',
                            };
                        });
                        setStockStatusMap(enriched);
                    })
                    .catch(() => { });
            }
        }
    }, [products?.length, effectiveBranchId, tenantId]);

    // ==================== SHIFT MANAGEMENT ====================

    useEffect(() => {
        if (shift && shift.status === 'open') {
            pos.setShift(shift.id, shift.branchId || effectiveBranchId);
            setShowShiftModal(false);
        } else if (shift === null && !shiftLoading) {
            setShowShiftModal(true);
        }
    }, [shift, shiftLoading, effectiveBranchId]);

    // ✅ NEW: Refetch shift when opening the close shift modal
    useEffect(() => {
        if (showShiftCloseModal) {
            refetchShift();
        }
    }, [showShiftCloseModal, refetchShift]);

    useEffect(() => { searchInputRef.current?.focus(); }, []);

    // ==================== MUTATIONS ====================

    // ✅ UPDATED: Open shift with selectedBranchId instead of effectiveBranchId
    const openShiftMutation = useMutation({
        mutationFn: (cash: string) => openShift(tenantId, selectedBranchId || undefined, cash, 'retail'),
        onSuccess: (data) => {
            pos.setShift(data.id, data.branchId || effectiveBranchId);
            setShowShiftModal(false);
            setOpeningCash('');
            addToast({ type: 'success', title: 'Shift Opened ✅', message: 'You can now start selling.' });
            refetchShift();
        },
        onError: async (err: any) => {
            const errMsg = err?.response?.data?.message || err?.message || '';
            const errCode = err?.response?.data?.error?.code || err?.code || '';
            const isAlreadyOpen =
                errCode === 'SHIFT_ALREADY_OPEN' ||
                errMsg.toLowerCase().includes('already have an open') ||
                errMsg.toLowerCase().includes('shift_already_open');

            if (isAlreadyOpen) {
                try {
                    const active = await getActiveShift(tenantId, 'retail');
                    if (active && active.status === 'open') {
                        pos.setShift(active.id, active.branchId || effectiveBranchId);
                        setShowShiftModal(false);
                        refetchShift();
                        setShowActiveShiftPrompt(true);
                        addToast({
                            type: 'info',
                            title: 'Active Shift Detected',
                            message: 'You already have an open shift. Resumed your active register session.',
                        });
                        return;
                    }
                } catch (e) {
                    console.error('Failed to auto-resume active shift:', e);
                }
            }
            addToast({ type: 'error', title: 'Failed to open shift', message: errMsg || 'Unable to open shift' });
        },
    });

    // ==================== PRODUCT SELECTION ====================

    const handleProductSelect = useCallback((product: any) => {
        // Special Handling for Hampers
        if (product.isHamper) {
            const hPrice = parseFloat(product.retailPrice || '0');
            pos.addItem({
                id: '',
                productId: product.originalHamper?.id || product.id,
                productName: product.name,
                variantId: undefined,
                variantName: undefined,
                uomId: 'uom-hamper',
                uomName: 'Hamper',
                uomAbbreviation: 'bundle',
                quantity: 1,
                unitPrice: hPrice,
                discountAmount: 0,
                taxAmount: hPrice * TAX_RATE,
                totalAmount: hPrice,
                notes: 'Gift Hamper Package',
            });
            playPosBeep('success');
            return;
        }

        if (product.isUnverified || product.isConfirmed === false) {
            addToast({
                type: 'error',
                title: '⚠️ Stock Not Confirmed',
                message: `${product.name} has not been counted and confirmed. Please confirm stock before selling.`,
                duration: 6000,
            });
            return;
        }
        if (product.isOutOfStock) {
            addToast({
                type: 'error',
                title: '❌ Out of Stock',
                message: `${product.name} has no available stock.`,
            });
            return;
        }
        const available = parseFloat(product.availableQuantity || '0');
        const cartItem = pos.cart.find(i => i.productId === product.id);
        const newQuantity = (cartItem?.quantity || 0) + 1;

        if (newQuantity > available) {
            addToast({
                type: 'error',
                title: '📦 Insufficient Stock',
                message: `Only ${available} units available. You already have ${cartItem?.quantity || 0} in cart.`,
            });
            return;
        }

        if (newQuantity > MAX_SINGLE_ITEM_QUANTITY) {
            addToast({
                type: 'warning',
                title: '⚠️ Large Quantity',
                message: `Adding ${newQuantity} units. Is this correct?`,
                duration: 5000,
            });
        }

        const uom = product.uoms?.[0];
        let price = parseFloat(product.retailPrice || '0');
        if (pos.saleType === 'wholesale' && product.wholesalePrice) price = parseFloat(product.wholesalePrice);
        if ((pos.saleType === 'clearance' || product.isClearance) && product.clearancePrice) {
            price = parseFloat(product.clearancePrice);
        }

        pos.addItem({
            id: '', productId: product.id, productName: product.name || 'Unknown',
            variantId: undefined, variantName: undefined,
            uomId: uom?.id || '', uomName: uom?.name || 'Unit', uomAbbreviation: uom?.abbreviation || 'pc',
            quantity: 1, unitPrice: price, discountAmount: 0,
            taxAmount: price * TAX_RATE, totalAmount: price,
            imageUrl: product.imageUrls?.[0],
            notes: (product.isClearance || pos.saleType === 'clearance') ? 'Clearance Sale' : undefined,
        });
        playPosBeep('success');
    }, [pos.saleType, pos.cart, addToast]);

    // ==================== BARCODE SCANNER HANDLER ====================

    const handleBarcodeScanned = useCallback(async (code: string) => {
        const rawCode = code.trim();
        if (!rawCode) return;

        if (!pos.activeShiftId) {
            playPosBeep('error');
            addToast({
                type: 'error',
                title: 'No Active Shift',
                message: 'Please open a shift before scanning or selling items.',
            });
            return;
        }

        const normalized = rawCode.toLowerCase();

        // 0. Check if item matches an active gift hamper
        const matchedHamper = ((hampers || []) as any[]).find((h: any) =>
            (h.barcode && h.barcode.toLowerCase() === normalized) ||
            (h.sku && h.sku.toLowerCase() === normalized) ||
            (h.slug && h.slug.toLowerCase() === normalized)
        );
        if (matchedHamper) {
            handleProductSelect({
                id: `hamper-${matchedHamper.id}`,
                name: `🎁 ${matchedHamper.name}`,
                retailPrice: matchedHamper.sellingPrice || '0',
                isHamper: true,
                originalHamper: matchedHamper,
                isConfirmed: true,
                availableQuantity: '99',
            });
            return;
        }

        // 1. Check if item is in currently loaded products (by barcode, sku, or id)
        let matchedProduct: any = displayProducts.find(
            p => (p.barcode && p.barcode.toLowerCase() === normalized) ||
                 (p.sku && p.sku.toLowerCase() === normalized) ||
                 p.id.toLowerCase() === normalized
        );

        // 2. If not found in loaded products, query direct barcode API
        if (!matchedProduct && tenantId) {
            try {
                const lookup = await lookupBarcode(tenantId, rawCode, effectiveBranchId);
                if (lookup) {
                    matchedProduct = {
                        id: lookup.productId,
                        name: lookup.productName,
                        retailPrice: lookup.retailPrice,
                        wholesalePrice: lookup.wholesalePrice,
                        clearancePrice: lookup.clearancePrice,
                        imageUrls: lookup.imageUrl ? [lookup.imageUrl] : [],
                        isOutOfStock: parseFloat(lookup.availableQuantity || '0') <= 0,
                        availableQuantity: lookup.availableQuantity,
                        isConfirmed: lookup.isConfirmed,
                        isUnverified: !lookup.isConfirmed,
                        confirmationStatus: lookup.confirmationStatus,
                        uoms: [{
                            id: lookup.uomId,
                            name: lookup.uomName,
                            abbreviation: lookup.uomAbbreviation,
                            isBaseUom: true,
                        }],
                    };
                }
            } catch {
                // Not found via barcode API
            }
        }

        // 3. Fallback: search via searchProducts if still not found
        if (!matchedProduct && tenantId) {
            try {
                const searchResults = await searchProducts(tenantId, rawCode, undefined, effectiveBranchId);
                if (searchResults && searchResults.length > 0) {
                    const candidate = searchResults.find(
                        p => (p.barcode && p.barcode.toLowerCase() === normalized) ||
                             (p.sku && p.sku.toLowerCase() === normalized)
                    ) || searchResults[0];

                    const stockMap = await batchCheckStockConfirmation(tenantId, [candidate.id], effectiveBranchId);
                    const status = stockMap[candidate.id];
                    const available = parseFloat(status?.availableQuantity || '0');
                    const hasRecord = status?.hasRecord ?? false;
                    const isConfirmed = status?.confirmed ?? false;

                    matchedProduct = {
                        ...candidate,
                        isLowStock: hasRecord && available > 0 && available <= LOW_STOCK_THRESHOLD,
                        isOutOfStock: hasRecord && available <= 0,
                        availableQuantity: status?.availableQuantity || '0',
                        isConfirmed: isConfirmed,
                        isUnverified: !hasRecord || !isConfirmed,
                        confirmationStatus: status?.confirmationStatus || 'pending',
                    };
                }
            } catch {
                // Search failed
            }
        }

        if (!matchedProduct) {
            playPosBeep('error');
            addToast({
                type: 'error',
                title: 'Barcode Not Found',
                message: `No product found matching "${rawCode}".`,
                duration: 4000,
            });
            return;
        }

        // Validate stock confirmation
        if (matchedProduct.isUnverified || matchedProduct.isConfirmed === false) {
            playPosBeep('error');
            addToast({
                type: 'error',
                title: '⚠️ Stock Not Confirmed',
                message: `${matchedProduct.name} has not been counted and confirmed. Please confirm stock before selling.`,
                duration: 6000,
            });
            return;
        }

        if (matchedProduct.isOutOfStock) {
            playPosBeep('error');
            addToast({
                type: 'error',
                title: '❌ Out of Stock',
                message: `${matchedProduct.name} has no available stock.`,
            });
            return;
        }

        const available = parseFloat(matchedProduct.availableQuantity || '0');
        const cartItem = pos.cart.find(i => i.productId === matchedProduct.id);
        const newQuantity = (cartItem?.quantity || 0) + 1;

        if (newQuantity > available) {
            playPosBeep('error');
            addToast({
                type: 'error',
                title: '📦 Insufficient Stock',
                message: `Only ${available} units available. You already have ${cartItem?.quantity || 0} in cart.`,
            });
            return;
        }

        if (newQuantity > MAX_SINGLE_ITEM_QUANTITY) {
            addToast({
                type: 'warning',
                title: '⚠️ Large Quantity',
                message: `Adding ${newQuantity} units. Is this correct?`,
                duration: 5000,
            });
        }

        const uom = matchedProduct.uoms?.[0];
        let price = parseFloat(matchedProduct.retailPrice || '0');
        if (pos.saleType === 'wholesale' && matchedProduct.wholesalePrice) price = parseFloat(matchedProduct.wholesalePrice);
        if (pos.saleType === 'clearance' && matchedProduct.clearancePrice) price = parseFloat(matchedProduct.clearancePrice);

        pos.addItem({
            id: '',
            productId: matchedProduct.id,
            productName: matchedProduct.name || 'Unknown',
            variantId: undefined,
            variantName: undefined,
            uomId: uom?.id || '',
            uomName: uom?.name || 'Unit',
            uomAbbreviation: uom?.abbreviation || 'pc',
            quantity: 1,
            unitPrice: price,
            discountAmount: 0,
            taxAmount: price * TAX_RATE,
            totalAmount: price,
            imageUrl: matchedProduct.imageUrls?.[0],
        });

        playPosBeep('success');

        if (cartItem) {
            addToast({
                type: 'success',
                title: 'Quantity Updated (+1) 🛒',
                message: `${matchedProduct.name} qty now ${newQuantity}`,
                duration: 2000,
            });
        } else {
            addToast({
                type: 'success',
                title: 'Added to Cart ✅',
                message: `${matchedProduct.name} added to cart`,
                duration: 2000,
            });
        }
    }, [pos.activeShiftId, pos.cart, pos.saleType, displayProducts, tenantId, effectiveBranchId, addToast]);

    // Global Hardware Barcode Scanner Listener
    useEffect(() => {
        let keyBuffer = '';
        let lastKeyTime = 0;
        let timeoutId: any = null;

        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            const activeTag = (document.activeElement?.tagName || '').toLowerCase();
            const isInputFocused = activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select';

            // If an input is focused, let that input handle keys directly
            if (isInputFocused) {
                return;
            }

            const now = Date.now();
            const timeDiff = now - lastKeyTime;

            if (e.key === 'Enter') {
                if (keyBuffer.length >= 3) {
                    e.preventDefault();
                    const barcode = keyBuffer.trim();
                    keyBuffer = '';
                    handleBarcodeScanned(barcode);
                }
                keyBuffer = '';
                return;
            }

            // Normal printable character
            if (e.key.length === 1) {
                if (lastKeyTime === 0 || timeDiff < 80) {
                    keyBuffer += e.key;
                } else {
                    keyBuffer = e.key;
                }
                lastKeyTime = now;

                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    keyBuffer = '';
                    lastKeyTime = 0;
                }, 150);
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
            window.removeEventListener('keydown', handleGlobalKeyDown);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [handleBarcodeScanned]);

    const canSellProduct = useCallback((product: any): boolean => {
        if (product.isUnverified || product.isConfirmed === false) return false;
        if (product.isOutOfStock) return false;
        return true;
    }, []);

    // ==================== CUSTOMER ====================

    const handleCustomerAttach = useCallback(async (phone: string) => {
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length < 10 || cleaned.length > 15) {
            addToast({ type: 'error', title: 'Invalid Phone', message: 'Phone number must be 10-15 digits.' });
            return null;
        }
        try {
            const customer = await findCustomerByPhone(tenantId, phone);
            if (customer) {
                const c = customer as any;
                const tier = (c.loyaltyTier || 'bronze') as 'bronze' | 'silver' | 'gold' | 'platinum';
                pos.setCustomer(c.id, `${c.firstName} ${c.lastName}`, {
                    id: c.id, name: `${c.firstName} ${c.lastName}`,
                    phone: c.phone || '', loyaltyCardNumber: c.loyaltyCardNumber || '',
                    loyaltyPoints: parseFloat(c.loyaltyPoints || '0'), loyaltyTier: tier,
                    storeCredit: parseFloat(c.storeCredit || '0'), tierDiscountPercent: 0,
                });
                addToast({ type: 'success', title: 'Customer Attached ✅', message: `${c.firstName} - ${tier} tier` });
                return customer;
            }
            addToast({ type: 'info', title: 'Customer Not Found', message: 'Create a new customer or try walk-in.' });
            return null;
        } catch {
            addToast({ type: 'error', title: 'Error', message: 'Could not look up customer.' });
            return null;
        }
    }, [tenantId, pos, addToast]);

    const handleQuickCreateCustomer = useCallback(async (form: { firstName: string; lastName: string; phone: string; dateOfBirth?: string }) => {
        if (!form.firstName.trim() || !form.lastName.trim()) {
            addToast({ type: 'error', title: 'Required Fields', message: 'First and last name are required.' });
            return null;
        }
        const cleaned = form.phone.replace(/\D/g, '');
        if (cleaned.length < 10) {
            addToast({ type: 'error', title: 'Invalid Phone', message: 'Phone number must be at least 10 digits.' });
            return null;
        }
        try {
            const { data } = await apiClient.post(`/tenants/${tenantId}/customers`, {
                ...form,
                phone: cleaned,
                branchId: effectiveBranchId || undefined,
            });
            const c = (data?.data || data) as any;
            const tier = (c.loyaltyTier || 'bronze') as 'bronze' | 'silver' | 'gold' | 'platinum';
            pos.setCustomer(c.id, `${c.firstName} ${c.lastName}`, {
                id: c.id, name: `${c.firstName} ${c.lastName}`,
                phone: c.phone || '', loyaltyCardNumber: c.loyaltyCardNumber || '',
                loyaltyPoints: parseFloat(c.loyaltyPoints || '0'), loyaltyTier: tier,
                storeCredit: parseFloat(c.storeCredit || '0'), tierDiscountPercent: 0,
            });
            addToast({ type: 'success', title: 'Customer Created ✅', message: `${c.firstName} ${c.lastName} added.` });
            return c;
        } catch (err: any) {
            addToast({ type: 'error', title: 'Failed', message: err?.message });
            return null;
        }
    }, [tenantId, pos, addToast]);

    // ==================== VOID SALE ====================

    const verifySupervisorPin = async (pin: string): Promise<boolean> => {
        try {
            const { data } = await apiClient.post(`/tenants/${tenantId}/auth/verify-pin`, { pin });
            return data?.valid || false;
        } catch { return false; }
    };

    const handleVoidSale = async (reason: string, supervisorPin?: string): Promise<boolean> => {
        if (!lastSaleId) return false;
        if (supervisorPin) {
            const isValid = await verifySupervisorPin(supervisorPin);
            if (!isValid) {
                addToast({ type: 'error', title: 'Unauthorized', message: 'Invalid supervisor PIN.' });
                return false;
            }
        }
        try {
            await apiClient.post(`/tenants/${tenantId}/sales/${lastSaleId}/void`, { reason });
            addToast({ type: 'success', title: 'Sale Voided', message: `${lastOrderNumber} has been voided.` });
            setLastSaleId(''); setLastOrderNumber(''); setLastTotal(0);
            return true;
        } catch {
            addToast({ type: 'error', title: 'Void Failed', message: 'Could not void this sale.' });
            return false;
        }
    };

    // ==================== PAYMENT COMPLETE ====================

    const handlePaymentComplete = useCallback(async (payments: PaymentItem[], discountTotal: string) => {
        if (!pos.activeShiftId) { addToast({ type: 'error', title: 'No active shift', message: 'Open a shift first.' }); return; }
        if (pos.cart.length === 0) { addToast({ type: 'error', title: 'Empty cart', message: 'Add items to cart first.' }); return; }

        if (payments.length === 0) {
            addToast({ type: 'error', title: 'No Payment', message: 'Please add at least one payment method.' });
            return;
        }

        const paymentTotal = payments.reduce((s, p) => s + (p.amount || 0), 0);
        if (Math.abs(paymentTotal - pos.grandTotal) > 1) {
            addToast({ type: 'warning', title: 'Payment Mismatch', message: `Total paid (₦${paymentTotal.toLocaleString()}) doesn't match amount due (₦${pos.grandTotal.toLocaleString()}).` });
        }

        setIsProcessing(true);
        try {
            const orderNumber = `INV-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;

            const enrichedPayments = payments.map(p => {
                if ((p.paymentMethod === 'bank_transfer' || p.paymentMethod === 'card') && !p.bankAccountId && terminalDefaultBankAccount) {
                    return { ...p, bankAccountId: terminalDefaultBankAccount };
                }
                if (p.paymentMethod === 'cash' && !p.bankAccountId && terminalDefaultCashAccount) {
                    return { ...p, bankAccountId: terminalDefaultCashAccount };
                }
                return p;
            });

            const sale = await directCheckout(tenantId, {
                branchId: effectiveBranchId,
                shiftId: pos.activeShiftId || undefined,
                items: pos.cart.map(item => ({
                    productId: item.productId,
                    variantId: item.variantId,
                    uomId: item.uomId,
                    quantity: item.quantity.toString(),
                    unitPrice: item.unitPrice.toString(),
                    discountAmount: (item.discountAmount || 0).toString(),
                })),
                payments: enrichedPayments,
                customerId: pos.customerId || undefined,
                saleType: pos.saleType,
                posTerminalId: currentTerminalId || undefined,
                discountTotal,
            });

            setLastSaleId(sale.id);
            setLastOrderNumber(sale.orderNumber || orderNumber);
            setLastTotal(pos.grandTotal);

            pos.clearCart();
            pos.setPaymentModalOpen(false);
            // ✅ FIXED: Call the refetch function (was missing parentheses)
            refetchShift();
            setShowReceiptModal(true);

            addToast({ type: 'success', title: 'Sale Complete! 🎉', message: `₦${pos.grandTotal.toLocaleString()} · ${orderNumber}` });
        } catch (err: any) {
            const msg = err?.response?.data?.error?.message || err?.message || 'Checkout failed';
            console.error('Checkout error:', err);
            addToast({ type: 'error', title: 'Checkout Failed', message: msg });
        } finally {
            setIsProcessing(false);
        }
    }, [tenantId, pos, effectiveBranchId, addToast, currentTerminalId, terminalDefaultBankAccount, terminalDefaultCashAccount, refetchShift]);

    // ==================== RENDER (Conditional returns AFTER all hooks) ====================

    if (isCheckingAccess) {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="text-center">
                    <Spinner size="xl" />
                    <p className="mt-4 text-slate-500 font-medium">Checking POS access...</p>
                </div>
            </div>
        );
    }

    if (!isSuperAdmin && posAccess !== 'retail' && posAccess !== 'both') {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="text-center max-w-sm">
                    <div className="w-20 h-20 rounded-3xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
                        <ShoppingCart size={40} className="text-slate-300" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-700 mb-2">Retail POS Access Denied</h2>
                    <p className="text-sm text-slate-400 mb-6">
                        You don't have access to the Retail POS terminal. Please contact your administrator.
                    </p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg"
                        type="button"
                    >
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (isKitchenView) {
        return <KitchenView onBack={() => setIsKitchenView(false)} tenantId={tenantId} branchId={effectiveBranchId} />;
    }

    if (!user) {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="text-center"><Spinner size="xl" /><p className="mt-4 text-slate-500 font-medium">Loading user session...</p></div>
            </div>
        );
    }

    if (shiftLoading) {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="text-center"><Spinner size="xl" /><p className="mt-4 text-slate-500 font-medium">Loading shift data...</p></div>
            </div>
        );
    }

    const needsSupervisorForVoid = lastTotal > VOID_SUPERVISOR_THRESHOLD;

    return (
        <div className="h-screen flex flex-col bg-slate-100 overflow-hidden">
            {!isOnline && (
                <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-bold flex items-center justify-center gap-2 flex-shrink-0">
                    <WifiOff size={16} /> Offline Mode — Transactions saved locally
                </div>
            )}

            <POSHeader
                shift={shift}
                branches={branches || []}
                selectedBranchId={effectiveBranchId}
                onOpenShift={() => setShowShiftModal(true)}
                onCloseShift={() => setShowShiftCloseModal(true)}
                onViewActiveShift={() => setShowActiveShiftPrompt(true)}
                isOnline={isOnline}
                showCustomerDisplay={showCustomerDisplay}
                onToggleCustomerDisplay={() => setShowCustomerDisplay(!showCustomerDisplay)}
                onToggleKitchenView={() => setIsKitchenView(!isKitchenView)}
                isKitchenView={isKitchenView}
                onCustomerClick={() => setShowCustomerModal(true)}
                lastSaleId={lastSaleId}
                onVoidLastSale={() => setShowVoidModal(true)}
            />

            <div className="flex-1 flex overflow-hidden">
                <CategoryPanel
                    categories={flatCategories}
                    selectedId={selectedCategoryId}
                    onSelect={setSelectedCategoryId}
                    isOpen={showCategoryPanel}
                    onToggle={() => setShowCategoryPanel(!showCategoryPanel)}
                />

                <div className="flex-1 flex flex-col overflow-hidden bg-white">
                    <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-shrink-0">
                        <button onClick={() => setShowCategoryPanel(!showCategoryPanel)}
                            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors" type="button"
                            title={showCategoryPanel ? 'Hide categories' : 'Show categories'}>
                            <SlidersHorizontal size={18} />
                        </button>

                        <ProductSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                            onSelect={handleProductSelect}
                            onBarcodeSubmit={handleBarcodeScanned}
                            disabled={!pos.activeShiftId}
                            inputRef={searchInputRef}
                        />

                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5 flex-shrink-0">
                            <button onClick={() => setGridLayout('grid')}
                                className={clsx('p-1.5 rounded-lg transition-all', gridLayout === 'grid' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-400')}
                                type="button" title="Grid view"><Grid3x3 size={14} /></button>
                            <button onClick={() => setGridLayout('list')}
                                className={clsx('p-1.5 rounded-lg transition-all', gridLayout === 'list' ? 'bg-white shadow-sm text-emerald-600' : 'text-slate-400')}
                                type="button" title="List view"><List size={14} /></button>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-slate-400 flex-shrink-0">
                            <span>{displayProducts.length} products</span>
                            {productsLoading && <Spinner size="xs" />}
                            {currentTerminalId && <span className="text-emerald-500 font-medium">🖥️</span>}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {productsLoading ? (
                            <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>
                        ) : displayProducts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                    </svg>
                                </div>
                                <p className="text-lg font-medium text-slate-500">{searchQuery ? 'No products match your search' : 'No confirmed products available'}</p>
                                <p className="text-sm mt-1 text-slate-400">{searchQuery ? 'Try a different search term' : 'Products need to be counted and confirmed before they appear here'}</p>
                            </div>
                        ) : gridLayout === 'grid' ? (
                            <div className="p-4">
                                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                                    {displayProducts.map((product, idx) => (
                                        <ProductCard key={product.id} product={product} onSelect={handleProductSelect} index={idx} />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-50">
                                {displayProducts.map((product, idx) => {
                                    const sellable = canSellProduct(product);
                                    const isUnverified = product.isUnverified;
                                    return (
                                        <div key={product.id}
                                            onClick={() => sellable && handleProductSelect(product)}
                                            className={clsx(
                                                'flex items-center gap-4 px-4 py-3 transition-colors',
                                                sellable && 'hover:bg-slate-50 cursor-pointer',
                                                !sellable && 'opacity-60 cursor-not-allowed bg-slate-50/50',
                                                product.isLowStock && sellable && 'bg-amber-50/30',
                                                isUnverified && 'bg-orange-50/30'
                                            )}>
                                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                {product.imageUrls?.[0] ? (
                                                    <img src={product.imageUrls[0]} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-sm font-bold text-slate-400">{product.name?.charAt(0)}</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-slate-900 truncate">{product.name}</p>
                                                <p className="text-xs text-slate-400">{product.brand || product.categoryName || '—'}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-bold text-emerald-600">₦{parseFloat(product.retailPrice || '0').toLocaleString()}</p>
                                                {product.isLowStock && sellable && <p className="text-[10px] text-amber-600 font-medium">{product.availableQuantity} left</p>}
                                            </div>
                                            <div className="text-center w-20">
                                                {!sellable ? (
                                                    isUnverified ? (
                                                        <span className="text-xs font-bold text-orange-600 flex items-center justify-center gap-1 bg-orange-100 px-2 py-1 rounded-lg">
                                                            <ShieldX size={12} /> Confirm Stock
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-bold text-rose-500 bg-rose-100 px-2 py-1 rounded-lg">Out</span>
                                                    )
                                                ) : product.isLowStock ? (
                                                    <button onClick={(e) => { e.stopPropagation(); handleProductSelect(product); }}
                                                        className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition-all" type="button">Low Stock</button>
                                                ) : (
                                                    <button onClick={(e) => { e.stopPropagation(); handleProductSelect(product); }}
                                                        className="px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-xs font-bold hover:from-emerald-700 transition-all" type="button">Add</button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="w-[400px] flex-shrink-0 shadow-xl z-10">
                    <CartPanel onCheckout={() => pos.setPaymentModalOpen(true)} disabled={!pos.activeShiftId || isProcessing} />
                </div>
            </div>

            {/* Modals */}
            <ShiftModal
                isOpen={showShiftModal}
                onClose={() => setShowShiftModal(false)}
                onConfirm={(cash) => openShiftMutation.mutate(cash)}
                isLoading={openShiftMutation.isPending}
                openingCash={openingCash}
                onOpeningCashChange={setOpeningCash}
                branches={branches || []}
                selectedBranchId={selectedBranchId}
                onBranchChange={setSelectedBranchId}   // 👈 ADDED
            />

            <ShiftCloseModal isOpen={showShiftCloseModal} onClose={() => setShowShiftCloseModal(false)}
                shift={shift}
                onShiftClosed={() => { pos.setShift(null, ''); pos.clearCart(); setShowShiftCloseModal(false); refetchShift(); }} />

            {/* Active Shift Resolution Prompt Modal */}
            <AnimatePresence>
                {showActiveShiftPrompt && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
                        <motion.div
                            initial={{ scale: 0.95, y: 15 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 15 }}
                            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200">
                            {/* Header */}
                            <div className="px-6 py-5 bg-gradient-to-r from-teal-800 to-slate-900 text-white flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-2xl bg-teal-500/20 border border-teal-400/30 flex items-center justify-center text-teal-300">
                                        <Clock size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-white">Active Shift Session</h3>
                                        <p className="text-xs text-teal-200/80">Shift is currently open and active</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowActiveShiftPrompt(false)}
                                    className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition">
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Details */}
                            <div className="p-6 space-y-4">
                                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200/80 space-y-2.5">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Status</span>
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Open & Active
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Opening Drawer Float</span>
                                        <span className="font-bold text-slate-800">₦{parseFloat(shift?.openingCash || '0').toLocaleString()}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Started</span>
                                        <span className="font-semibold text-slate-700">
                                            {shift?.openedAt ? new Date(shift.openedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Earlier today'}
                                        </span>
                                    </div>
                                    {shift?.totalSales && (
                                        <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200/60">
                                            <span className="text-slate-500">Recorded Sales</span>
                                            <span className="font-black text-emerald-600">₦{parseFloat(shift.totalSales).toLocaleString()}</span>
                                        </div>
                                    )}
                                </div>

                                <p className="text-xs text-slate-600 leading-relaxed">
                                    To start a new shift, you must first properly count and close the existing shift drawer. Or you can continue using this shift to sell items immediately.
                                </p>

                                <div className="space-y-2 pt-2">
                                    <Button
                                        onClick={() => setShowActiveShiftPrompt(false)}
                                        className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-2.5 rounded-xl shadow-sm">
                                        Use This Shift & Continue Selling
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowActiveShiftPrompt(false);
                                            setShowShiftCloseModal(true);
                                        }}
                                        className="w-full border-amber-300 text-amber-800 hover:bg-amber-50 font-bold py-2.5 rounded-xl">
                                        Close Shift & Reconcile Drawer
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <CustomerModal isOpen={showCustomerModal} onClose={() => setShowCustomerModal(false)}
                onSearch={handleCustomerAttach} onCreateCustomer={handleQuickCreateCustomer}
                currentCustomer={pos.customerInfo} onRemoveCustomer={() => pos.setCustomer(null, null)} />

            <PaymentModal isOpen={pos.isPaymentModalOpen} onClose={() => pos.setPaymentModalOpen(false)}
                onComplete={async (payments, discountTotal) => { await handlePaymentComplete(payments, discountTotal); }}
                isLoading={isProcessing} terminalDefaultBankAccount={terminalDefaultBankAccount}
                branchId={effectiveBranchId} />

            <ReceiptModal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
                onNewSale={() => { setShowReceiptModal(false); pos.clearCart(); }}
                tenantId={tenantId} saleId={lastSaleId} total={lastTotal} />

            <VoidSaleModal isOpen={showVoidModal} onClose={() => setShowVoidModal(false)}
                onVoid={handleVoidSale} saleTotal={lastTotal} orderNumber={lastOrderNumber}
                requiresSupervisor={needsSupervisorForVoid} />
        </div>
    );
}